import { FC, useEffect, useState } from 'react';
import Container from '@mui/material/Container';
import { ScreenState } from '@enums/index';
import { useAppDispatch, useAppSelector } from '@hooks/store';
import { Box, Breadcrumbs, Grid, Typography } from '@mui/material';
import PortfolioSelection from '@components/portfolioSelection';
import TransactionsDataGrid from './TransactionsDataGrid';
import { useWindow } from '@src/app/hooks/useWindow';
import { useAppContext } from '@src/app/hooks/useAppContext';
import { getTransactionsAsync } from '@src/app/store/reducers/transactions/thunks';
import { selectTransactions } from '@src/app/store/reducers/transactions/selectors';
import { selectPortfolios } from '@src/app/store/reducers/portfolios/selectors';
import { LocalizationProvider } from '@mui/lab';
import AdapterLuxon from '@mui/lab/AdapterLuxon';
import DateRangePicker from '@components/dateRange';
import { DateRange } from '@src/app/store';
import { setTransactionDataRange } from '@src/app/store/reducers/transactions';
import { Home } from '@mui/icons-material';
import { homeUrl } from '@src/app/utils/UrlBuilder';
import { useHistory } from 'react-router';
import { PageContent } from '@components/pageContent';
import TransactionCard from './TransactionCard';

const Transactions: FC = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const [currentPageSize, setCurrentPageSize] = useState<number>(25);

  const { resizing } = useWindow();
  const { transitioningMenu } = useAppContext();

  const { data, state, dateRange, filteredIds, filteredItem } = useAppSelector(selectTransactions);
  const { portfoliosSelected, portfolioInfoState } = useAppSelector(selectPortfolios);

  const updateDateRange = (dates: DateRange) => {
    dispatch(setTransactionDataRange(dates));
  };

  const fetchTransactionsAsync = async () => {
    await dispatch(getTransactionsAsync({ fromDate: dateRange.dateFrom, toDate: dateRange.dateTo, clientPortfolios: portfoliosSelected }));
  };

  useEffect(() => {
    fetchTransactionsAsync();
  }, [portfoliosSelected, dateRange]);

  const RenderDatatable: FC = (): JSX.Element => {
    if (resizing || transitioningMenu) {
      return <></>;
    }
    return (
      <TransactionsDataGrid
        clearFilteringButton
        data={data?.transactions ?? []}
        filterIds={filteredIds}
        filteredItem={filteredItem}
        currentPageSize={currentPageSize}
        setCurrentPageSize={setCurrentPageSize}
      />
    );
  };

  return (
    <Container maxWidth={false}>
      <Box display="flex" flexDirection="column" gap="24px" mb={6}>
        <Box display="flex" gap="16px" mt={2} sx={{ alignItems: { xs: 'flex-start', md: 'center' }, flexDirection: { xs: 'column', md: 'row' } }}>
          <Breadcrumbs>
            <Typography
              onClick={() => history.push(homeUrl())}
              sx={{
                display: 'flex',
                alignItems: 'center',
                ':hover': {
                  cursor: 'pointer',
                },
              }}
              color="inherit"
            >
              <Home sx={{ mr: 0.5 }} fontSize="small" />
              Dashboard
            </Typography>
            <Typography fontWeight={600} fontSize="20px" color="#222222">
              Transactions
            </Typography>
          </Breadcrumbs>

          <Box flex={1} />

          <Box display="flex" gap="16px">
            <LocalizationProvider dateAdapter={AdapterLuxon}>
              <DateRangePicker isDateOnly dateRange={dateRange} setDateRange={(range: DateRange) => updateDateRange(range)} />
            </LocalizationProvider>
            <PortfolioSelection allowCombining={false} disabled={state === ScreenState.Loading || portfolioInfoState === ScreenState.Loading} />
          </Box>
        </Box>

        <Grid container spacing={2}>
          <TransactionCard
            name="Contributions"
            filterIds={data?.portfolioTransactionsInfo?.contributionsTransactions ?? []}
            value={data?.portfolioTransactionsInfo?.contributions ?? 0}
            filterValue="Contribution"
          />
          <TransactionCard
            name="Income"
            filterIds={data?.portfolioTransactionsInfo?.incomeTransactions ?? []}
            value={data?.portfolioTransactionsInfo?.income ?? 0}
            filterValue="Income"
          />
          <TransactionCard
            name="Withdrawals"
            filterIds={data?.portfolioTransactionsInfo?.withdrawalsTransactions ?? []}
            value={data?.portfolioTransactionsInfo?.withdrawals ?? 0}
            filterValue="Withdrawal"
          />
          <TransactionCard
            name="Expenses"
            filterIds={data?.portfolioTransactionsInfo?.expensesTransactions ?? []}
            value={data?.portfolioTransactionsInfo?.expenses ?? 0}
            filterValue="Expense"
          />
          <TransactionCard
            name="Buys"
            filterIds={data?.portfolioTransactionsInfo?.buysTransactions ?? []}
            value={data?.portfolioTransactionsInfo?.buys ?? 0}
            filterValue="Buy"
          />
          <TransactionCard
            name="Sells"
            filterIds={data?.portfolioTransactionsInfo?.sellsTransactions ?? []}
            value={data?.portfolioTransactionsInfo?.sells ?? 0}
            filterValue="Sell"
          />
        </Grid>

        <PageContent skeletons={1} isLoading={portfolioInfoState === ScreenState.Loading || state === ScreenState.Loading}>
          <RenderDatatable />
        </PageContent>
      </Box>
    </Container>
  );
};

export default Transactions;
